// services/galleryService.js
import api from './api';

// Cache for image and video URLs to prevent repeated API calls
const imageUrlCache = new Map();
const videoUrlCache = new Map();

export const galleryService = {
    /**
     * Get applicants with infinite scroll, filtering, and sorting
     * @param {string} companyId - Company ID (required)
     * @param {number} limit - Number of items per page (default: 20)
     * @param {number|null} lastRowNumber - Last row number from previous page (for pagination)
     * @param {string|null} search - Search term for filtering by name, reference, or occupation
     * @param {string|null} filterBy - Simple filter by field (e.g., nationality)
     * @param {string|null} sortBy - Sort field (name, age, salary, nationality, experience, reference, occupation)
     * @param {string|null} sortDirection - Sort direction (asc or desc)
     * @param {Object|null} filters - Advanced filter options
     * @param {string} filters.nationality - Filter by nationality
     * @param {string} filters.religion - Filter by religion
     * @param {string} filters.maritalStatus - Filter by marital status
     * @param {number} filters.minAge - Minimum age
     * @param {number} filters.maxAge - Maximum age
     * @param {number} filters.minSalary - Minimum salary
     * @param {number} filters.maxSalary - Maximum salary
     * @param {number} filters.minExperience - Minimum experience
     * @param {number} filters.maxExperience - Maximum experience
     * @param {string[]} filters.skills - Array of skills (ironing, sewing, babysitting, cleaning, washing, childcare, arabiccooking)
     * @param {boolean} filters.hasVideo - Filter by video availability
     * @param {boolean} filters.hasPhoto - Filter by photo availability
     * @returns {Promise<Object>} - { items, nextRowNumber, hasMore, limit, totalCount }
     */
    getApplicantsInfinite: async (
        companyId,
        limit = 20,
        lastRowNumber = null,
        search = null,
        filterBy = null,
        sortBy = null,
        sortDirection = null,
        filters = null
    ) => {
        try {
            // Validate companyId
            if (!companyId || companyId === 'undefined' || companyId === 'null') {
                throw new Error('Company ID is required');
            }

            const params = new URLSearchParams({
                companyId: companyId,
                limit: limit,
            });
            
            // Pagination
            if (lastRowNumber !== null && lastRowNumber !== undefined) {
                params.append('lastRowNumber', lastRowNumber);
            }
            
            // Search
            if (search) params.append('search', search);
            
            // Simple filter
            if (filterBy) params.append('filterBy', filterBy);
            
            // Sorting
            if (sortBy) params.append('sortBy', sortBy);
            if (sortDirection) params.append('sortDirection', sortDirection);
            
            // ✅ Advanced filters
            if (filters) {
                if (filters.nationality) params.append('nationality', filters.nationality);
                if (filters.religion) params.append('religion', filters.religion);
                if (filters.maritalStatus) params.append('maritalStatus', filters.maritalStatus);
                if (filters.minAge !== null && filters.minAge !== undefined) params.append('minAge', filters.minAge);
                if (filters.maxAge !== null && filters.maxAge !== undefined) params.append('maxAge', filters.maxAge);
                if (filters.minSalary !== null && filters.minSalary !== undefined) params.append('minSalary', filters.minSalary);
                if (filters.maxSalary !== null && filters.maxSalary !== undefined) params.append('maxSalary', filters.maxSalary);
                if (filters.minExperience !== null && filters.minExperience !== undefined) params.append('minExperience', filters.minExperience);
                if (filters.maxExperience !== null && filters.maxExperience !== undefined) params.append('maxExperience', filters.maxExperience);
                if (filters.skills && filters.skills.length > 0) params.append('skills', filters.skills.join(','));
                if (filters.hasVideo !== null && filters.hasVideo !== undefined) params.append('hasVideo', filters.hasVideo);
                if (filters.hasPhoto !== null && filters.hasPhoto !== undefined) params.append('hasPhoto', filters.hasPhoto);
            }

            console.log(`📡 Fetching with params: ${params.toString()}`);

            const response = await api.get(`/api/ApplicantGallery/infinite?${params}`);
            
            console.log('📡 API Response:', {
                itemsCount: response.data.items?.length || 0,
                hasMore: response.data.hasMore,
                nextRowNumber: response.data.nextRowNumber,
                totalCount: response.data.totalCount
            });
            
            return {
                items: response.data.items || [],
                nextRowNumber: response.data.nextRowNumber || null,
                hasMore: response.data.hasMore || false,
                limit: response.data.limit || limit,
                totalCount: response.data.totalCount || 0
            };
        } catch (error) {
            console.error('❌ Error fetching applicants:', error);
            throw error;
        }
    },

    /**
     * Get single applicant by ID
     */
    getApplicantById: async (id) => {
        try {
            if (!id) {
                throw new Error('Applicant ID is required');
            }

            const response = await api.get(`/api/ApplicantGallery/${id}`);
            return response.data;
        } catch (error) {
            console.error('❌ Error fetching applicant:', error);
            throw error;
        }
    },

    /**
     * Get image URL with caching
     */
    getImageUrl: async (photoKey) => {
        try {
            if (!photoKey) return null;
            
            // Check cache first
            if (imageUrlCache.has(photoKey)) {
                return imageUrlCache.get(photoKey);
            }
            
            const response = await api.get('/api/ApplicantGallery/get-image-url', {
                params: { photoKey }
            });
            
            const url = response.data?.url || null;
            imageUrlCache.set(photoKey, url);
            return url;
        } catch (error) {
            // Cache null to prevent retries
            imageUrlCache.set(photoKey, null);
            if (error.response?.status !== 404) {
                console.error('❌ Error fetching image URL:', photoKey, error);
            }
            return null;
        }
    },

    /**
     * Get video URL with caching
     */
    getVideoUrl: async (videoKey) => {
        try {
            if (!videoKey) return null;
            
            // Check cache first
            if (videoUrlCache.has(videoKey)) {
                return videoUrlCache.get(videoKey);
            }
            
            const response = await api.get('/api/ApplicantGallery/get-video-url', {
                params: { videoKey }
            });
            
            const url = response.data?.url || null;
            videoUrlCache.set(videoKey, url);
            return url;
        } catch (error) {
            videoUrlCache.set(videoKey, null);
            if (error.response?.status !== 404) {
                console.error('❌ Error fetching video URL:', videoKey, error);
            }
            return null;
        }
    },

    /**
     * Batch get video URLs
     */
    getVideoUrls: async (videoKeys) => {
        try {
            if (!videoKeys || videoKeys.length === 0) return {};
            
            // Check cache for each key
            const uncachedKeys = videoKeys.filter(key => !videoUrlCache.has(key));
            
            if (uncachedKeys.length === 0) {
                const result = {};
                videoKeys.forEach(key => {
                    result[key] = videoUrlCache.get(key);
                });
                return result;
            }
            
            const response = await api.post('/api/ApplicantGallery/get-video-urls', uncachedKeys);
            
            if (response.data?.urls) {
                Object.entries(response.data.urls).forEach(([key, url]) => {
                    videoUrlCache.set(key, url);
                });
                return response.data.urls;
            }
            return {};
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('❌ Error fetching video URLs:', error);
            }
            return {};
        }
    },

    /**
     * Preload video in background
     */
    preloadVideo: async (videoKey) => {
        try {
            if (!videoKey) return null;

            const url = await galleryService.getVideoUrl(videoKey);
            if (url) {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.src = url;
                video.load();
                console.log('📡 Video preloaded in background');
            }
            return url;
        } catch (error) {
            console.error('❌ Error preloading video:', error);
            return null;
        }
    },

    /**
     * Clear cache
     */
    clearCache: () => {
        imageUrlCache.clear();
        videoUrlCache.clear();
        console.log('🧹 Cache cleared');
    },

    /**
     * Get total count
     */
    getTotalCount: async (companyId) => {
        try {
            const response = await api.get('/api/ApplicantGallery/count', {
                params: { companyId }
            });
            return response.data;
        } catch (error) {
            console.error('❌ Error getting total count:', error);
            return 0;
        }
    },

    /**
     * Pre-warm cache
     */
    preWarmCache: async (companyId) => {
        try {
            if (!companyId) return;
            
            await api.post('/api/ApplicantGallery/prewarm', null, {
                params: { companyId }
            });
            console.log('🔥 Cache pre-warming started');
        } catch (error) {
            console.error('❌ Error pre-warming cache:', error);
        }
    },

    /**
     * Clear cache for company
     */
    clearCompanyCache: async (companyId) => {
        try {
            await api.post('/api/ApplicantGallery/clear-cache', {
                companyId: companyId
            });
            galleryService.clearCache();
            console.log('🧹 Company cache cleared');
        } catch (error) {
            console.error('❌ Error clearing cache:', error);
        }
    },

    /**
     * Helper: Check if applicant has video
     */
    hasVideo: (applicant) => {
        return !!(applicant?.videoUrl || 
                 applicant?.hasVideo || 
                 applicant?.interviewKey || 
                 applicant?.InterviewKey);
    },

    /**
     * Helper: Check if applicant has photo
     */
    hasPhoto: (applicant) => {
        return !!(applicant?.photoUrl || 
                 applicant?.hasPhoto || 
                 applicant?.photoKey || 
                 applicant?.PhotoKey);
    },

    /**
     * Helper: Get available filter options
     * This can be extended to fetch from API
     */
    getFilterOptions: () => {
        return {
            nationalities: ['Ethiopia', 'Philippines', 'India', 'Sri Lanka', 'Nepal', 'Egypt', 'Kenya', 'Uganda'],
            religions: ['Christian', 'Muslim', 'Hindu', 'Buddhist', 'Other'],
            maritalStatuses: ['Single', 'Married', 'Divorced', 'Widowed'],
            skills: ['Ironing', 'Sewing', 'BabySitting', 'Cleaning', 'Washing', 'ChildCare', 'ArabicCooking'],
            sortOptions: [
                { value: 'name', label: 'Name' },
                { value: 'age', label: 'Age' },
                { value: 'salary', label: 'Salary' },
                { value: 'nationality', label: 'Nationality' },
                { value: 'experience', label: 'Experience' },
                { value: 'reference', label: 'Reference No' },
                { value: 'occupation', label: 'Occupation' }
            ]
        };
    }
};

export default galleryService;